import type {
	SnipeTransferPlayersFacets,
	TransferPlayerState,
} from "@/api/custom_types";
import { toFacetFilterOptions } from "@/components/base";
import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import type { FacetCounts } from "@/hooks/useFacetFilters";
import { useTranslation } from "@/hooks/useTranslation";
import {
	type FieldConfig,
	type FilterConfig,
	GenericFilters,
} from "@st-marketplace/ui";
import { useMemo } from "react";
import { z } from "zod";

const filterSchema = z.object({
	playerNickname: z.string().trim().optional(),
	activationState: z.array(z.string()),
	rarityName: z.array(z.string()),
	clubName: z.array(z.string()),
	leagueName: z.array(z.string()),
	nationName: z.array(z.string()),
	transferPlayers: z.enum(["include", "exclude"]).optional(),
});
export type FilterFormData = z.infer<typeof filterSchema>;

interface TransferPlayersTableFilters {
	playerNickname: string;
	activationState: string[];
	rarityName: string[];
	clubName: string[];
	leagueName: string[];
	nationName: string[];
	transferPlayers: string;
}

function toTableFilters(values?: FilterFormData): TransferPlayersTableFilters {
	return {
		playerNickname: values?.playerNickname ?? "",
		activationState: values?.activationState ? [...values.activationState] : [],
		rarityName: values?.rarityName ? [...values.rarityName] : [],
		clubName: values?.clubName ? [...values.clubName] : [],
		leagueName: values?.leagueName ? [...values.leagueName] : [],
		nationName: values?.nationName ? [...values.nationName] : [],
		transferPlayers: values?.transferPlayers ?? "",
	};
}

interface STAdminTransferPlayersFilterProps {
	onSearch: (values: FilterFormData) => void;
	defaultValues?: FilterFormData;
	facetCounts: FacetCounts<SnipeTransferPlayersFacets>;
	/** Player database only: include or exclude the cards already in transfer players. */
	showTransferPlayersFilter?: boolean;
}

type FacetList =
	| FacetCounts<SnipeTransferPlayersFacets>["rarity_names"]
	| FacetCounts<SnipeTransferPlayersFacets>["club_names"]
	| FacetCounts<SnipeTransferPlayersFacets>["league_names"]
	| FacetCounts<SnipeTransferPlayersFacets>["nation_names"];

const toOptions = (facets: FacetList | undefined) =>
	toFacetFilterOptions(facets ?? [], (name) => name);

/** Same labels the table's state chip uses. */
const ACTIVATION_STATE_LABEL_KEYS = {
	ACTIVE: "admin.transferPlayers.activationState.active",
	DEACTIVATED_MANUALLY:
		"admin.transferPlayers.activationState.deactivatedManually",
	DEACTIVATED_TOO_MANY_ERROR:
		"admin.transferPlayers.activationState.deactivatedTooManyErrors",
	DEACTIVATED_PRICE_UNFAVORABLE:
		"admin.transferPlayers.activationState.deactivatedPriceUnfavorable",
} as const satisfies Record<TransferPlayerState, string>;

export const STAdminTransferPlayersFilter: React.FC<
	STAdminTransferPlayersFilterProps
> = ({ onSearch, defaultValues, facetCounts, showTransferPlayersFilter }) => {
	const { t } = useTranslation();

	const { filters, onFiltersChange, clearFilters } =
		useAutoApplyFilters<TransferPlayersTableFilters>({
			initialFilters: toTableFilters(defaultValues),
			onApply: (next) =>
				onSearch({
					playerNickname: next.playerNickname || undefined,
					activationState: next.activationState,
					rarityName: next.rarityName,
					clubName: next.clubName,
					leagueName: next.leagueName,
					nationName: next.nationName,
					transferPlayers:
						next.transferPlayers === "exclude" ? "exclude" : undefined,
				}),
			textFilterKeys: ["playerNickname"],
		});

	const activationStateItems = useMemo(
		() =>
			toFacetFilterOptions(facetCounts.activation_states ?? [], (state) =>
				t(
					ACTIVATION_STATE_LABEL_KEYS[state as TransferPlayerState] ??
						"admin.transferPlayers.activationState.deactivatedManually",
				),
			),
		[facetCounts.activation_states, t],
	);

	const rarityNameItems = useMemo(
		() => toOptions(facetCounts.rarity_names),
		[facetCounts.rarity_names],
	);

	const clubNameItems = useMemo(
		() => toOptions(facetCounts.club_names),
		[facetCounts.club_names],
	);

	const leagueNameItems = useMemo(
		() => toOptions(facetCounts.league_names),
		[facetCounts.league_names],
	);

	const nationNameItems = useMemo(
		() => toOptions(facetCounts.nation_names),
		[facetCounts.nation_names],
	);

	const config = useMemo<FilterConfig<TransferPlayersTableFilters>>(() => {
		const fields: FieldConfig<TransferPlayersTableFilters>[] = [
			{
				key: "playerNickname",
				type: "search",
				placeholder: t("admin.transferPlayers.filters.playerNickname"),
			},
			// Suggestions reuse this component and have no activation state,
			// so the field only appears when the facet actually has values.
			...(activationStateItems.length
				? ([
						{
							key: "activationState",
							type: "multi-select",
							placeholder: t("admin.transferPlayers.filters.activationStates"),
							options: activationStateItems,
						},
					] as FieldConfig<TransferPlayersTableFilters>[])
				: []),
			{
				key: "rarityName",
				type: "multi-select",
				placeholder: t("admin.transferPlayers.filters.rarityNames"),
				options: rarityNameItems,
				searchable: true,
			},
			{
				key: "clubName",
				type: "multi-select",
				placeholder: t("admin.transferPlayers.filters.clubNames"),
				options: clubNameItems,
				searchable: true,
			},
			{
				key: "leagueName",
				type: "multi-select",
				placeholder: t("admin.transferPlayers.filters.leagueNames"),
				options: leagueNameItems,
				searchable: true,
			},
			{
				key: "nationName",
				type: "multi-select",
				placeholder: t("admin.transferPlayers.filters.nationNames"),
				options: nationNameItems,
				searchable: true,
			},
		];

		if (showTransferPlayersFilter) {
			fields.push({
				key: "transferPlayers",
				type: "select",
				placeholder: t("admin.transferPlayers.database.transferPlayersFilter"),
				options: [
					{
						value: "include",
						label: t("admin.transferPlayers.database.includeTransferPlayers"),
					},
					{
						value: "exclude",
						label: t("admin.transferPlayers.database.excludeTransferPlayers"),
					},
				],
			});
		}

		return {
			fields,
			searchDebounceMs: 0,
			showClearButton: true,
		};
	}, [
		t,
		activationStateItems,
		rarityNameItems,
		clubNameItems,
		leagueNameItems,
		nationNameItems,
		showTransferPlayersFilter,
	]);

	const handleClearFilters = () => {
		clearFilters(toTableFilters());
	};

	return (
		<GenericFilters
			config={config}
			filters={filters}
			onFiltersChange={onFiltersChange}
			onClearFilters={handleClearFilters}
		/>
	);
};
